/**
 * useViews - Hook for managing views (saved workspace configurations)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type {
  View,
  ViewsListResponse,
  ViewResponse,
  CreateViewRequest,
  UpdateViewRequest,
} from './types';

export interface UseViewsOptions {
  siteId: string;
  contentType: string;
  enabled?: boolean;
}

export interface UseViewsReturn {
  // Data
  views: View[];
  templates: View[];
  isLoading: boolean;
  error: Error | null;

  // Mutations
  createView: (data: CreateViewRequest) => Promise<View>;
  updateView: (id: string, data: UpdateViewRequest) => Promise<View>;
  deleteView: (id: string) => Promise<void>;
  applyTemplate: (templateId: string, targetSiteId: string) => Promise<View>;
  recordUsage: (viewId: string) => Promise<void>;

  // Mutation states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export function useViews({ siteId, contentType, enabled = true }: UseViewsOptions): UseViewsReturn {
  const queryClient = useQueryClient();
  const queryKey = ['views', siteId, contentType];

  // Fetch views for this site and content type
  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await api.get<ViewsListResponse>(
        `/api/v1/views?siteId=${siteId}&contentType=${contentType}`
      );
      return result;
    },
    enabled: enabled && !!siteId && !!contentType,
    staleTime: 60 * 1000, // Cache for 1 minute
  });

  // Create view mutation
  const createMutation = useMutation({
    mutationFn: async (request: CreateViewRequest) => {
      const result = await api.post<ViewResponse>('/api/v1/views', request);
      return result.view;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Update view mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateViewRequest }) => {
      const result = await api.put<ViewResponse>(`/api/v1/views/${id}`, data);
      return result.view;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Delete view mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/views/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Apply template mutation
  const applyTemplateMutation = useMutation({
    mutationFn: async ({ templateId, targetSiteId }: { templateId: string; targetSiteId: string }) => {
      const result = await api.post<ViewResponse>(`/api/v1/views/${templateId}/apply/${targetSiteId}`, {});
      return result.view;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Record usage mutation (fire and forget)
  const recordUsageMutation = useMutation({
    mutationFn: async (viewId: string) => {
      await api.post(`/api/v1/views/${viewId}/use`, {});
    },
    // No need to invalidate - just tracking usage
  });

  return {
    views: data?.views || [],
    templates: data?.templates || [],
    isLoading,
    error: error as Error | null,

    createView: createMutation.mutateAsync,
    updateView: (id: string, updateData: UpdateViewRequest) =>
      updateMutation.mutateAsync({ id, data: updateData }),
    deleteView: deleteMutation.mutateAsync,
    applyTemplate: (templateId: string, targetSiteId: string) =>
      applyTemplateMutation.mutateAsync({ templateId, targetSiteId }),
    recordUsage: recordUsageMutation.mutateAsync,

    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

// Helper hook to get the default view for a content type
export function useDefaultView(siteId: string, contentType: string): View | null {
  const { views } = useViews({ siteId, contentType });
  return views.find(v => v.is_default) || null;
}
